import { ClassEnum, LOCALE_OPTIONS, SETTING_TYPE } from '@/constants/enum';
import { previewOptionSelector } from '@/redux/slice/preview.slice';
import { widgetColorSelector, widgetSettingSelector } from '@/redux/slice/widget.slice';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CalendarIcon from '@/asset/icons/calendar';
import { getColor } from '@/helpers';
import { handleSavePreviewDateTimeSettings, previewDateTimeSelector } from '@/redux/slice/previewDateTime.slice';
import { widgetTextDetailSelector } from '@/redux/slice/widgetText.slice';
import { formatDateTime, localeMapping } from '@/utils';
import CalendarPopover from './CalendarPopover';
import CustomCalendar, { ICalendarProps } from './CustomCalendar';
import useRequiredMessage from '@/hooks/useRequiredMessage';

const DayPicker = () => {
  const [isShowContent, setIsShowContent] = useState(false);
  const widgetTextDetail = useSelector(widgetTextDetailSelector);
  const widgetShippingTexts = useMemo(
    () => widgetTextDetail.texts?.find((item) => item.type === SETTING_TYPE.SHIPPING),
    [widgetTextDetail.texts],
  );

  const widgetDeliveryTexts = useMemo(
    () => widgetTextDetail.texts?.find((item) => item.type === SETTING_TYPE.DELIVERY),
    [widgetTextDetail.texts],
  );

  const widgetPickupTexts = useMemo(
    () => widgetTextDetail.texts?.find((item) => item.type === SETTING_TYPE.PICKUP),
    [widgetTextDetail.texts],
  );

  const widgetSettings = useSelector(widgetSettingSelector);
  const previewOptions = useSelector(previewOptionSelector);
  const previewDateTimes = useSelector(previewDateTimeSelector);
  const widgetColors = useSelector(widgetColorSelector);
  const dispatch = useDispatch();
  const [month, setMonth] = useState(new Date());

  const { isShowRequiredMessage } = useRequiredMessage();

  const onSelected = (date: Date) => {
    dispatch(handleSavePreviewDateTimeSettings({ selectedDate: date }));
    setIsShowContent(false);
  };

  const dateTitle = useMemo(() => {
    switch (previewOptions.methods.find((item) => item.selected)?.key) {
      case SETTING_TYPE.DELIVERY:
        return widgetDeliveryTexts?.labelAndButtons?.dateTitle;
      case SETTING_TYPE.PICKUP:
        return widgetPickupTexts?.labelAndButtons?.dateTitle;
      case SETTING_TYPE.SHIPPING:
        return widgetShippingTexts?.labelAndButtons?.dateTitle;
      default:
        return '';
    }
  }, [
    previewOptions.methods,
    widgetDeliveryTexts?.labelAndButtons?.dateTitle,
    widgetPickupTexts?.labelAndButtons?.dateTitle,
    widgetShippingTexts?.labelAndButtons?.dateTitle,
  ]);

  const dateInputTitle = useMemo(() => {
    switch (previewOptions.methods.find((item) => item.selected)?.key) {
      case SETTING_TYPE.DELIVERY:
        return widgetDeliveryTexts?.labelAndButtons?.captionDateTitle;
      case SETTING_TYPE.PICKUP:
        return widgetPickupTexts?.labelAndButtons?.captionDateTitle;
      case SETTING_TYPE.SHIPPING:
        return widgetShippingTexts?.labelAndButtons?.captionDateTitle;
      default:
        return '';
    }
  }, [
    previewOptions.methods,
    widgetDeliveryTexts?.labelAndButtons?.captionDateTitle,
    widgetPickupTexts?.labelAndButtons?.captionDateTitle,
    widgetShippingTexts?.labelAndButtons?.captionDateTitle,
  ]);

  const calendarProps: ICalendarProps = {
    selectedDate: previewDateTimes.previewDateTime.selectedDate,
    onSelected,
    month,
    setMonth,
  };

  return (
    <div className={ClassEnum.DayPickerContainer} id="dd-container">
      <label
        className={ClassEnum.DayPickerLabel}
        style={{ color: getColor(widgetSettings.colorScheme, widgetColors, 'header', 'dateTitleColor') }}
        id="dd-label"
      >
        {dateTitle}
      </label>
      {widgetSettings.customizations.alwaysShowCalendar ? (
        <CustomCalendar
          {...calendarProps}
          style={{
            border:
              isShowRequiredMessage && !previewDateTimes.previewDateTime.selectedDate
                ? `2px solid ${getColor(widgetSettings.colorScheme, widgetColors, 'body', 'errorColor')}`
                : undefined,
            // animation: isShowRequiredMessage ? 'border-error-blink 2s infinite' : 'none',
          }}
        />
      ) : (
        <CalendarPopover
          isDisabled={false}
          isShowContent={isShowContent}
          setIsShowContent={setIsShowContent}
          color={getColor(widgetSettings.colorScheme, widgetColors, 'header', 'inputTextDateColor')}
          backgroundColor={getColor(widgetSettings.colorScheme, widgetColors, 'header', 'inputBackground')}
          buttonContent={
            <div className={ClassEnum.CalendarPopoverButton}>
              <CalendarIcon
                id="dd-calendar-icon"
                width={(20 * widgetSettings.sizes.inputFontSize) / 14}
                height={(20 * widgetSettings.sizes.inputFontSize) / 14}
              />
              {previewDateTimes.previewDateTime.selectedDate ? (
                formatDateTime(
                  previewDateTimes.previewDateTime.selectedDate,
                  widgetSettings.customizations.dateFormat,
                  localeMapping[widgetTextDetail.calendarLocale as LOCALE_OPTIONS],
                )
              ) : (
                <span>{dateInputTitle}</span>
              )}
            </div>
          }
          borderRadius={widgetSettings.sizes.borderRadius}
          styles={{
            button: {
              border:
                isShowRequiredMessage && !previewDateTimes.previewDateTime.selectedDate
                  ? `2px solid ${getColor(widgetSettings.colorScheme, widgetColors, 'body', 'errorColor')}`
                  : undefined,
              // animation: isShowRequiredMessage ? 'border-error-blink 2s infinite' : 'none',
            },
          }}
        >
          <CustomCalendar {...calendarProps} />
        </CalendarPopover>
      )}
    </div>
  );
};

export default DayPicker;
